import { CustomAddressInputField, DatePicker, fullPhoneParser, ISO_DATE_FORMAT, PhoneInputField, strIsNullOrEmpty, TextField, tryParseJSONObject } from '@fox/atoms';
import { CountryCode, E164Number } from 'libphonenumber-js';
import moment, { Moment } from 'moment';
import React from 'react';
import 'react-phone-number-input/style.css';
import styled from 'styled-components';
import { QuestionStyleTypes, useQuestionStyle } from '../../../form-runtime-context';
import { CreatePersonBlockMetadata } from '../contracts';
import { useI18n } from 'fox-extensions';

function getFieldInformation(type?: string, meta?: CreatePersonBlockMetadata) {
	if (!type || !meta) {
		return { placeholder: undefined, required: false };
	}

	const placeholder = meta.editFields?.[type]?.placeholder ?? undefined;
	const required = meta.editFields?.[type]?.meta?.required ?? false;
	return { placeholder, required };
}
function getLabel(placeholder?: string, required?: boolean) {
	const label = placeholder ? `${placeholder}${required ? '*' : ''}` : '';
	return label;
}

const typesToTrim = ['email'];

export const TextChange = (props: { value: string; onChange: (val: string) => void; id: string; type?: string; meta?: CreatePersonBlockMetadata }) => {
	const { fontSize, fontFamily, color, fontStyle, textDecoration, fontWeight } = useQuestionStyle(QuestionStyleTypes.answer, {}, props.id);
	const { placeholder, required } = getFieldInformation(props.type, props.meta);

	const minHeight = fontSize ? 'unset' : '33px';
	return (
		<TextField
			fullWidth
			style={{ fontSize, fontFamily, color, fontStyle, textDecoration, minHeight, fontWeight }}
			value={props.value ?? ''}
			onChange={c => {
				if (props.type && typesToTrim.includes(props.type)) {
					props.onChange(c.target.value.trim());
				} else {
					props.onChange(c.target.value);
				}
			}}
			variant="outlined"
			placeholder={placeholder}
			required={required}
			size="small"
			sx={{
				input: {
					// boxSizing: 'inherit'
				},
				'.MuiInputBase-root': {
					color,
					fontSize,
					fontStyle,
					fontFamily,
					textDecoration,
					fontWeight
				}
			}}
		/>
	);
};

export const InputFieldWrapper = styled.div<{
	$fontSize?: string | number;
	$fontFamily?: string;
	$color?: string;
	$fontStyle?: string;
	$textDecoration?: string;
	$fontWeight?: number;
}>`
	.link-button {
		${props => (props.$fontSize ? `font-size: ${props.$fontSize} !important;` : '')}
		${props => (props.$fontFamily ? `font-family: ${props.$fontFamily}!important;` : '')}
        ${props => (props.$color ? `color: ${props.$color}!important;` : '')}
        ${props => (props.$fontStyle ? `font-style: ${props.$fontStyle}!important;` : '')}
        ${props => (props.$textDecoration ? `text-decoration: ${props.$textDecoration}!important;` : '')}
        ${props => (props.$fontWeight ? `font-weight: ${props.$fontWeight}!important;` : '')}
	}

	.MuiInputBase-root {
		${props => (props.$fontSize ? `font-size: ${props.$fontSize};` : '')}
		${props => (props.$fontFamily ? `font-family: ${props.$fontFamily};` : '')}
        ${props => (props.$color ? `color: ${props.$color};` : '')}
        ${props => (props.$fontStyle ? `font-style: ${props.$fontStyle};` : '')}
        ${props => (props.$textDecoration ? `text-decoration: ${props.$textDecoration};` : '')}
        ${props => (props.$fontWeight ? `font-weight: ${props.$fontWeight};` : '')}
	}
	.MuiSelect-select {
		padding: calc(13px - 0.5em) 14px;
	}
	fieldset {
		${props => (props.$fontSize ? `font-size: ${props.$fontSize};` : '')}
	}
`;

export const DateChange = (props: { value: string; onChange: (val: string) => void; id: string; type?: string; meta?: CreatePersonBlockMetadata }) => {
	const { value, onChange, id, type, meta } = props;
	const { placeholder, required } = getFieldInformation(type, meta);
	const { fontSize, fontFamily, color, fontStyle, textDecoration, fontWeight } = useQuestionStyle(QuestionStyleTypes.answer, {}, id);
	const label = getLabel(placeholder, required);

	return (
		<InputFieldWrapper $fontSize={fontSize} $fontFamily={fontFamily} $fontStyle={fontStyle} $textDecoration={textDecoration} $color={color} $fontWeight={fontWeight}>
			<DatePicker
				fullWidth
				size="small"
				label={label}
				sx={{ fontSize, fontFamily, color, fontStyle, textDecoration }}
				value={value ? moment(value) : null}
				onChange={(date: Moment | null) => {
					onChange(date ? moment(date).format(ISO_DATE_FORMAT) : '');
				}}
			/>
		</InputFieldWrapper>
	);
};

export const AddressChange = (props: { addressTypeId?: number; value: string; type?: string; meta?: CreatePersonBlockMetadata; onChange: (val: string) => void; id: string }) => {
	const { value, onChange, id, meta, type } = props;

	const parsed = tryParseJSONObject(value);
	const { fontSize, fontFamily, color, fontStyle, textDecoration, fontWeight } = useQuestionStyle(QuestionStyleTypes.answer, {}, id);
	const { primaryColour } = useQuestionStyle(QuestionStyleTypes.general, {}, id);
	const isPostcode = type ? meta?.editFields?.[type]?.meta?.mode === 'postcode' : false;

	if (isPostcode) return <TextChange value={parsed?.formatted} onChange={pc => onChange(JSON.stringify({ formatted: pc, postcode_only: true }))} id={id} />;

	return (
		<InputFieldWrapper $fontSize={fontSize} $fontFamily={fontFamily} $color={color} $fontStyle={fontStyle} $textDecoration={textDecoration} $fontWeight={fontWeight}>
			<CustomAddressInputField
				colour={primaryColour}
				value={parsed}
				onChange={c => {
					onChange(JSON.stringify(c));
				}}
			/>
		</InputFieldWrapper>
	);
};

export const PhoneInputFieldWrapper = styled.div<{
	$fontSize?: string | number;
	$fontFamily?: string;
	$color?: string;
	$fontStyle?: string;
	$textDecoration?: string;
	$fontWeight?: number;
}>`
	.PhoneInputCountry {
		margin-right: 5px;
		${props => (props.$fontSize ? `font-size: ${props.$fontSize};` : '')}
		${props => (props.$fontFamily ? `font-family: ${props.$fontFamily}!important;` : '')}
        ${props => (props.$color ? `color: ${props.$color}!important;` : '')}
        ${props => (props.$fontStyle ? `font-style: ${props.$fontStyle};` : '')}
        ${props => (props.$textDecoration ? `text-decoration: ${props.$textDecoration};` : '')}
        ${props => (props.$fontWeight ? `font-weight: ${props.$fontWeight};` : '')}
	}
	input {
		${props => (props.$fontSize ? `font-size: ${props.$fontSize};` : '')}
		${props => (props.$fontFamily ? `font-family: ${props.$fontFamily};` : '')}
        ${props => (props.$color ? `color: ${props.$color};` : '')}
        ${props => (props.$fontStyle ? `font-style: ${props.$fontStyle};` : '')}
        ${props => (props.$textDecoration ? `text-decoration: ${props.$textDecoration};` : '')}
        ${props => (props.$fontWeight ? `font-weight: ${props.$fontWeight};` : '')}
        min-height: 33px;
		padding: 3px 14px;
		transition: none !important;
		border-color: rgba(0, 0, 0, 0.23);
	}
	input:focus {
		border-width: 2px !important;
		${props => (props.theme?.colors?.grey?.[200] ? `border-color: ${props.theme.colors.grey[200]};` : '')}
		transition: none !important;
		padding-top: 2px;
		padding-bottom: 2px;
	}
	input:hover {
		${props => (props.theme?.colors?.font ? `border-color: ${props.theme.colors.font};` : '')}
		transition: none;
	}
`;

interface PhoneChangeProps {
	value: string;
	onChange: (val: string | null, valid?: boolean) => void;
	id: string;
	type: 'phone' | 'mobile';
	onBlur?: (number: string, valid: boolean) => void;
	meta?: CreatePersonBlockMetadata;
}

export const PhoneChange = ({ value, onChange, onBlur, id, type, meta }: PhoneChangeProps) => {
	const i18n = useI18n();
	const [phoneCountry, setPhoneCountry] = React.useState<CountryCode | undefined>(i18n.phoneCountryCode);
	const [number, setNumber] = React.useState<E164Number>((value ?? '') as E164Number);
	const [error, setError] = React.useState<string | undefined>(undefined);
	const { placeholder, required } = getFieldInformation(type, meta);
	const label = getLabel(placeholder, required);

	const phoneType = type === 'phone' ? 'landline' : 'mobile';
	const errorMessage = `Invalid ${phoneType} number`;

	const updateState = (isValid: boolean, inputValue: E164Number) => {
		if (isValid) {
			setError(undefined);
			setNumber(inputValue);
			onChange(inputValue ?? null, isValid);
		} else {
			setError(errorMessage);
			onChange(null, false);
		}
	};

	const handleNumberInput = (inputValue: E164Number | null | undefined) => {
		if (strIsNullOrEmpty(inputValue)) {
			updateState(false, '' as E164Number);
			return;
		}

		try {
			const parsedNumber = fullPhoneParser(inputValue, phoneCountry);
			const checkPhoneType = phoneType === 'landline' ? 'FIXED_LINE' : 'MOBILE';
			const isValid = parsedNumber.isValid && parsedNumber.type === checkPhoneType;

			// Type detection requires the max metadata, which is massive so we do not do this
			// https://gitlab.com/catamphetamine/libphonenumber-js#min-vs-max-vs-mobile-vs-core

			updateState(isValid, inputValue);
		} catch (e) {
			setError(errorMessage);
		}
	};

	const { fontSize, fontFamily, color, fontStyle, textDecoration, fontWeight } = useQuestionStyle(QuestionStyleTypes.answer, {}, id);

	return (
		<PhoneInputFieldWrapper $fontSize={fontSize} $fontFamily={fontFamily} $color={color} $fontStyle={fontStyle} $textDecoration={textDecoration} $fontWeight={fontWeight}>
			<PhoneInputField
				placeholder={label ?? `Enter ${phoneType} number`}
				defaultCountry={i18n.phoneCountryCode}
				country={phoneCountry}
				onCountryChanged={code => setPhoneCountry(code)}
				value={number}
				onChange={handleNumberInput}
				error={error}
				onBlur={() => {
					onBlur?.(number, !error);
				}}
			/>
		</PhoneInputFieldWrapper>
	);
};
